import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import axios from 'axios';
import AppBranding from './AppBranding';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import {
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Trophy,
  DollarSign,
  Clock,
  PieChart as PieChartIcon,
  BarChart3,
  RefreshCw,
  Award,
  Target,
  Percent,
  FileText,
  Download,
  Printer,
  Star,
} from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { toast } from 'sonner';
import { TradeDetailModal } from './dashboard/TradeDetailModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API_V2 = `${BACKEND_URL}/api/v2`;

// Distinct colors for each cryptocurrency - easily distinguishable
const COIN_COLORS = {
  'BTC': '#F7931A',    // Bitcoin orange
  'ETH': '#627EEA',    // Ethereum purple-blue
  'SOL': '#9945FF',    // Solana purple
  'LTC': '#BFBBBB',    // Litecoin silver
  'DOGE': '#C2A633',   // Dogecoin gold
  'BCH': '#8DC351',    // Bitcoin Cash green
  'XRP': '#00C3FF',    // XRP cyan/blue
  'SHIB': '#FFA409',   // Shiba Inu orange-gold
};

// Fallback colors if coin not in map
const FALLBACK_COLORS = ['#00FF41', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8'];
const HOUR_COLORS = '#00FF41';

const UserStatsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState(null);
  const [trades, setTrades] = useState([]);
  const [lifetimeStats, setLifetimeStats] = useState(null);
  const [profitByHour, setProfitByHour] = useState([]);
  const [profitByPair, setProfitByPair] = useState([]);
  const [badges, setBadges] = useState(null);
  const [showcaseBadges, setShowcaseBadges] = useState([]);
  
  // Tax report state
  const [showTaxDialog, setShowTaxDialog] = useState(false);
  const [taxYear, setTaxYear] = useState(new Date().getFullYear());
  const [taxState, setTaxState] = useState('');
  const [taxReport, setTaxReport] = useState(null);
  const [generatingReport, setGeneratingReport] = useState(false);
  
  // Trade detail modal state
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [feeRate, setFeeRate] = useState(0.0125);
  
  // Enhanced tax options
  const [costBasisMethod, setCostBasisMethod] = useState('fifo');
  const [useCustomDates, setUseCustomDates] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [coinFilter, setCoinFilter] = useState('');
  const [exportFormat, setExportFormat] = useState('standard');

  const generateTaxReport = async () => {
    setGeneratingReport(true);
    try {
      let url = `${API_V2}/user/stats/tax-report?cost_basis_method=${costBasisMethod}`;
      
      if (useCustomDates && startDate && endDate) {
        url += `&start_date=${startDate}&end_date=${endDate}`;
      } else {
        url += `&year=${taxYear}`;
      }
      
      if (taxState) url += `&state=${taxState}`;
      if (coinFilter) url += `&coin_filter=${coinFilter}`;
      
      const response = await axios.get(url, { withCredentials: true });
      setTaxReport(response.data);
      toast.success('Tax report generated successfully');
    } catch (error) {
      console.error('Failed to generate tax report:', error);
      toast.error('Failed to generate tax report');
    } finally {
      setGeneratingReport(false);
    }
  };

  const downloadCSV = async (format) => {
    try {
      let url = `${API_V2}/user/stats/tax-report/csv?format=${format}&cost_basis_method=${costBasisMethod}`;
      
      if (useCustomDates && startDate && endDate) {
        url += `&start_date=${startDate}&end_date=${endDate}`;
      } else {
        url += `&year=${taxYear}`;
      }
      
      if (coinFilter) url += `&coin_filter=${coinFilter}`;
      
      const response = await axios.get(url, { 
        withCredentials: true,
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `tax_report_${taxYear || 'custom'}_${format}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(downloadUrl);
      toast.success(`${format.toUpperCase()} CSV downloaded`);
    } catch (error) {
      console.error('Failed to download CSV:', error);
      toast.error('Failed to download CSV');
    }
  };

  const downloadTaxReport = () => {
    if (!taxReport) return;
    
    // Create a formatted text report
    let reportText = `
================================================================================
                    CRYPTOCURRENCY TAX REPORT - ${taxReport.tax_year || 'Custom Range'}
================================================================================
Generated: ${new Date(taxReport.report_generated).toLocaleString()}
User: ${taxReport.user?.name || 'N/A'} (${taxReport.user?.email || 'N/A'})
Date Range: ${taxReport.date_range?.start} to ${taxReport.date_range?.end}
Cost Basis Method: ${taxReport.cost_basis_method || 'FIFO'}
${taxReport.coin_filter ? `Filtered by: ${taxReport.coin_filter}` : ''}

DISCLAIMER: ${taxReport.disclaimer}

================================================================================
                           FEDERAL TAX SUMMARY
================================================================================
Total Transactions:     ${taxReport.federal_summary?.total_transactions || 0}
Total Buys:             ${taxReport.federal_summary?.total_buys || 0}
Total Sells:            ${taxReport.federal_summary?.total_sells || 0}

Total Proceeds:         $${(taxReport.federal_summary?.total_proceeds || 0).toLocaleString('en-US', {minimumFractionDigits: 2})}
Total Cost Basis:       $${(taxReport.federal_summary?.total_cost_basis || 0).toLocaleString('en-US', {minimumFractionDigits: 2})}

NET REALIZED GAIN/LOSS: $${(taxReport.federal_summary?.net_realized_gain_loss || 0).toLocaleString('en-US', {minimumFractionDigits: 2})}
  - Short-term (<1yr):  $${(taxReport.federal_summary?.short_term_gain_loss || 0).toLocaleString('en-US', {minimumFractionDigits: 2})}
  - Long-term (>1yr):   $${(taxReport.federal_summary?.long_term_gain_loss || 0).toLocaleString('en-US', {minimumFractionDigits: 2})}

Wash Sale Disallowed:   $${(taxReport.federal_summary?.wash_sale_disallowed || 0).toLocaleString('en-US', {minimumFractionDigits: 2})}
Adjusted Gain/Loss:     $${(taxReport.federal_summary?.adjusted_gain_loss || 0).toLocaleString('en-US', {minimumFractionDigits: 2})}

================================================================================
                        ESTIMATED TAX LIABILITY
================================================================================
Federal Estimate:       $${(taxReport.estimated_tax?.federal_estimate || 0).toLocaleString('en-US', {minimumFractionDigits: 2})}
Note: ${taxReport.estimated_tax?.note || 'Estimate based on standard tax brackets'}

================================================================================
                         UNREALIZED GAINS/LOSSES
================================================================================
`;

    if (taxReport.unrealized_gains && taxReport.unrealized_gains.length > 0) {
      taxReport.unrealized_gains.forEach(item => {
        reportText += `${item.coin.padEnd(6)} ${item.quantity.toString().padEnd(15)} @ $${item.current_price.toFixed(2).padEnd(12)} Value: $${item.current_value.toFixed(2).padEnd(12)} Cost: $${item.cost_basis.toFixed(2).padEnd(12)} Unrealized: $${item.unrealized_gain_loss.toFixed(2)}\n`;
      });
      reportText += `\nTotal Unrealized: $${(taxReport.total_unrealized_gain_loss || 0).toLocaleString('en-US', {minimumFractionDigits: 2})}\n`;
    } else {
      reportText += `No current holdings\n`;
    }

    reportText += `
================================================================================
                           STATE TAX INFORMATION
================================================================================
${taxReport.state_info?.state ? `State: ${taxReport.state_info.state}
Has Income Tax: ${taxReport.state_info.has_income_tax ? 'Yes' : 'No'}
Note: ${taxReport.state_info.note}` : 'No state specified'}

================================================================================
                         QUARTERLY BREAKDOWN
================================================================================
Q1 (Jan-Mar):  $${(taxReport.quarterly_breakdown?.Q1_Jan_Mar || 0).toLocaleString('en-US', {minimumFractionDigits: 2})}
Q2 (Apr-Jun):  $${(taxReport.quarterly_breakdown?.Q2_Apr_Jun || 0).toLocaleString('en-US', {minimumFractionDigits: 2})}
Q3 (Jul-Sep):  $${(taxReport.quarterly_breakdown?.Q3_Jul_Sep || 0).toLocaleString('en-US', {minimumFractionDigits: 2})}
Q4 (Oct-Dec):  $${(taxReport.quarterly_breakdown?.Q4_Oct_Dec || 0).toLocaleString('en-US', {minimumFractionDigits: 2})}

================================================================================
                      CRYPTOCURRENCY BREAKDOWN
================================================================================
`;

    if (taxReport.crypto_summary) {
      Object.entries(taxReport.crypto_summary).forEach(([coin, data]) => {
        reportText += `
${coin}:
  Buys: ${data.total_buys} (${data.quantity_bought} ${coin}, $${data.amount_bought_usd})
  Sells: ${data.total_sells} (${data.quantity_sold} ${coin}, $${data.amount_sold_usd})
  Realized Gain/Loss: $${data.realized_gain_loss}
`;
      });
    }

    reportText += `
================================================================================
                    FORM 8949 ENTRIES (First 100)
================================================================================
Description                Date Acquired  Date Sold      Proceeds    Cost Basis   Gain/Loss    Term
----------------------------------------------------------------------------------------------------
`;

    if (taxReport.form_8949_entries) {
      taxReport.form_8949_entries.slice(0, 100).forEach(entry => {
        reportText += `${(entry.description || '').padEnd(26)} ${(entry.date_acquired || 'Various').padEnd(14)} ${(entry.date_sold || '').padEnd(14)} $${(entry.proceeds || 0).toFixed(2).padStart(10)} $${(entry.cost_basis || 0).toFixed(2).padStart(10)} $${(entry.gain_loss || 0).toFixed(2).padStart(10)} ${entry.term || ''}\n`;
      });
    }

    reportText += `
----------------------------------------------------------------------------------------------------
Total Form 8949 Entries: ${taxReport.total_form_8949_entries || 0}

================================================================================
                         WASH SALES DETECTED
================================================================================
`;

    if (taxReport.wash_sales && taxReport.wash_sales.length > 0) {
      taxReport.wash_sales.forEach(ws => {
        reportText += `${ws.coin}: Sold ${ws.sell_date} (loss $${ws.sell_loss}) -> Bought ${ws.buy_date} (${ws.days_apart} days apart)\n`;
      });
      reportText += `\nTotal potential wash sales: ${taxReport.total_wash_sales}\n`;
    } else {
      reportText += `No wash sales detected\n`;
    }

    reportText += `
================================================================================
                              NOTES
================================================================================
- Simulation trades (${taxReport.simulation_note?.simulation_trades || 0}) are NOT included in this report
- ${taxReport.simulation_note?.note || ''}
- This report uses ${taxReport.cost_basis_method || 'FIFO'} accounting method
- Short-term: held less than 1 year | Long-term: held more than 1 year
================================================================================
`;

    // Download the file
    const blob = new Blob([reportText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `BitBot_Tax_Report_${taxYear || 'custom'}_${costBasisMethod}_${taxState || 'Federal'}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Tax report downloaded');
  };

  const printTaxReport = () => {
    if (!taxReport) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>BitBot V3.9.32 Tax Report ${taxYear}</title>
          <style>
            body { font-family: monospace; padding: 20px; background: #fff; color: #000; }
            h1, h2 { border-bottom: 2px solid #000; padding-bottom: 10px; }
            .section { margin: 20px 0; }
            table { width: 100%; border-collapse: collapse; margin: 10px 0; }
            th, td { border: 1px solid #000; padding: 8px; text-align: left; }
            th { background: #f0f0f0; }
            .disclaimer { background: #fff3cd; padding: 10px; border: 1px solid #ffc107; margin: 10px 0; }
            .positive { color: green; }
            .negative { color: red; }
          </style>
        </head>
        <body>
          <h1>CRYPTOCURRENCY TAX REPORT - ${taxYear}</h1>
          <p>Generated: ${new Date(taxReport.report_generated).toLocaleString()}</p>
          <p>User: ${taxReport.user?.name || 'N/A'} (${taxReport.user?.email || 'N/A'})</p>
          <div class="disclaimer"><strong>DISCLAIMER:</strong> ${taxReport.disclaimer}</div>
          
          <h2>Federal Tax Summary</h2>
          <table>
            <tr><td>Total Transactions</td><td>${taxReport.federal_summary?.total_transactions || 0}</td></tr>
            <tr><td>Total Buys</td><td>${taxReport.federal_summary?.total_buys || 0}</td></tr>
            <tr><td>Total Sells</td><td>${taxReport.federal_summary?.total_sells || 0}</td></tr>
            <tr><td>Total Proceeds</td><td>$${(taxReport.federal_summary?.total_proceeds || 0).toFixed(2)}</td></tr>
            <tr><td>Total Cost Basis</td><td>$${(taxReport.federal_summary?.total_cost_basis || 0).toFixed(2)}</td></tr>
            <tr><td><strong>Net Realized Gain/Loss</strong></td><td class="${(taxReport.federal_summary?.net_realized_gain_loss || 0) >= 0 ? 'positive' : 'negative'}"><strong>$${(taxReport.federal_summary?.net_realized_gain_loss || 0).toFixed(2)}</strong></td></tr>
            <tr><td>Short-term Gain/Loss</td><td>$${(taxReport.federal_summary?.short_term_gain_loss || 0).toFixed(2)}</td></tr>
            <tr><td>Long-term Gain/Loss</td><td>$${(taxReport.federal_summary?.long_term_gain_loss || 0).toFixed(2)}</td></tr>
          </table>
          
          ${taxReport.state_info?.state ? `
          <h2>State Tax Information (${taxReport.state_info.state})</h2>
          <p>Has Income Tax: ${taxReport.state_info.has_income_tax ? 'Yes' : 'No'}</p>
          <p>${taxReport.state_info.note}</p>
          ` : ''}
          
          <h2>Quarterly Breakdown</h2>
          <table>
            <tr><th>Quarter</th><th>Gain/Loss</th></tr>
            <tr><td>Q1 (Jan-Mar)</td><td>$${(taxReport.quarterly_breakdown?.Q1_Jan_Mar || 0).toFixed(2)}</td></tr>
            <tr><td>Q2 (Apr-Jun)</td><td>$${(taxReport.quarterly_breakdown?.Q2_Apr_Jun || 0).toFixed(2)}</td></tr>
            <tr><td>Q3 (Jul-Sep)</td><td>$${(taxReport.quarterly_breakdown?.Q3_Jul_Sep || 0).toFixed(2)}</td></tr>
            <tr><td>Q4 (Oct-Dec)</td><td>$${(taxReport.quarterly_breakdown?.Q4_Oct_Dec || 0).toFixed(2)}</td></tr>
          </table>
          
          <h2>Cryptocurrency Breakdown</h2>
          <table>
            <tr><th>Coin</th><th>Buys</th><th>Sells</th><th>Bought ($)</th><th>Sold ($)</th><th>Gain/Loss</th></tr>
            ${Object.entries(taxReport.crypto_summary || {}).map(([coin, data]) => `
              <tr>
                <td>${coin}</td>
                <td>${data.total_buys}</td>
                <td>${data.total_sells}</td>
                <td>$${data.amount_bought_usd.toFixed(2)}</td>
                <td>$${data.amount_sold_usd.toFixed(2)}</td>
                <td class="${data.realized_gain_loss >= 0 ? 'positive' : 'negative'}">$${data.realized_gain_loss.toFixed(2)}</td>
              </tr>
            `).join('')}
          </table>
          
          <p style="margin-top: 30px; font-size: 12px;">
            <strong>Note:</strong> Simulation trades (${taxReport.simulation_note?.simulation_trades || 0}) are NOT included.
            This report uses FIFO accounting. All gains classified as short-term.
          </p>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const fetchAllData = useCallback(async () => {
    if (!user) return;
    
    try {
      setRefreshing(true);
      
      // Fetch performance stats
      // Use higher limit for trades to get accurate hourly profit data
      const [statsRes, tradesRes, lifetimeRes] = await Promise.all([
        axios.get(`${API_V2}/user/stats/performance/stats`, { withCredentials: true }),
        axios.get(`${API_V2}/user/trades?limit=5000`, { withCredentials: true }),
        axios.get(`${API_V2}/user/stats/lifetime`, { withCredentials: true }),
      ]);
      
      setStats(statsRes.data);

      // Fetch badges
      if (user?.user_id) {
        try {
          const badgeRes = await axios.get(`${API_V2}/user/badges/${user.user_id}`, { withCredentials: true });
          if (badgeRes.data?.success) {
            setBadges(badgeRes.data);
            setShowcaseBadges(badgeRes.data.showcase_badges || []);
          }
        } catch (e) {
          console.error('Failed to fetch badges:', e);
        }
      }
      // Only keep latest 50 for the trade history table display
      setTrades((tradesRes.data.trades || []).slice(0, 50));
      setLifetimeStats(lifetimeRes.data);
      
      // Process profit by hour using ALL trades (not just the 50 shown in table)
      const allTrades = tradesRes.data.trades || [];
      const hourlyProfit = {};
      for (let i = 0; i < 24; i++) {
        hourlyProfit[i] = 0;
      }
      
      const sellTrades = allTrades.filter(t => t.type === 'SELL');
      sellTrades.forEach(trade => {
        if (trade.timestamp) {
          const date = new Date(trade.timestamp);
          const hour = date.getHours();
          hourlyProfit[hour] += trade.profit || 0;
        }
      });
      
      const hourlyData = Object.entries(hourlyProfit).map(([hour, profit]) => ({
        hour: `${hour}:00`,
        profit: parseFloat(profit.toFixed(4)),
        hourNum: parseInt(hour),
      }));
      setProfitByHour(hourlyData);
      
      // Process profit by pair
      if (statsRes.data.profit_by_pair) {
        const pairData = Object.entries(statsRes.data.profit_by_pair)
          .map(([pair, profit], index) => {
            const coinName = pair.replace('-USD', '');
            return {
              name: coinName,
              value: parseFloat(Math.abs(profit).toFixed(4)),
              profit: parseFloat(profit.toFixed(4)),
              // Use coin-specific color, fallback to array if not found
              fill: COIN_COLORS[coinName] || FALLBACK_COLORS[index % FALLBACK_COLORS.length],
            };
          })
          .filter(p => p.value > 0);
        setProfitByPair(pairData);
      }
      
    } catch (error) {
      console.error('Error fetching user stats:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Fetch user's dynamic fee rate for trade detail modal
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_V2}/user/fee-info`, { withCredentials: true });
        if (res.data?.taker_fee) setFeeRate(res.data.taker_fee);
      } catch { /* noop */ }
    })();
  }, []);

  const formatCurrency = (value) => {
    if (value === undefined || value === null) return '$0.00';
    return `$${parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
  };

  const toggleShowcaseBadge = async (badgeId) => {
    let updated;
    if (showcaseBadges.includes(badgeId)) {
      updated = showcaseBadges.filter(id => id !== badgeId);
    } else {
      if (showcaseBadges.length >= 3) {
        toast.error('Maximum 3 showcase badges. Unpin one first.');
        return;
      }
      updated = [...showcaseBadges, badgeId];
    }
    setShowcaseBadges(updated);
    try {
      await axios.put(`${API_V2}/user/showcase-badges`, { badge_ids: updated }, { withCredentials: true });
      toast.success(updated.includes(badgeId) ? 'Badge pinned to showcase' : 'Badge unpinned');
    } catch (e) {
      setShowcaseBadges(showcaseBadges);
      toast.error('Failed to update showcase');
    }
  };

  const formatProfit = (value) => {
    if (value === undefined || value === null) return '$0.00';
    const prefix = value >= 0 ? '+' : '';
    return `${prefix}${formatCurrency(value)}`;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-[#001a00] border border-[#00FF41] p-2 text-xs">
          <p className="text-[#00FF41]">{label}</p>
          <p className={payload[0].value >= 0 ? 'text-[#00FF41]' : 'text-red-500'}>
            Profit: {formatProfit(payload[0].value)}
          </p>
        </div>
      );
    }
    return null;
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <p className="text-[#00FF41]">Please log in to view your stats.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-[#00FF41]">
      <AppBranding />
      
      {/* Header */}
      <header className="border-b border-[#003B00] p-4" style={{ background: 'rgba(5, 5, 5, 0.9)' }}>
        <div className="max-w-[1920px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/')}
              className="matrix-btn rounded-none text-xs h-8"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-3 w-3 mr-1" strokeWidth={1.5} />
              BACK TO DASHBOARD
            </Button>
            <h1 className="text-xl font-bold uppercase tracking-widest glow-text">
              USER STATISTICS
            </h1>
          </div>
          
          <div className="flex items-center gap-4">
            {user && (
              <div className="flex items-center gap-2 px-3 py-1 border border-[#00FF41] bg-[#003B00]/20">
                {user.picture && (
                  <img 
                    src={user.picture} 
                    alt={user.name} 
                    className="h-8 w-8 rounded-full border border-[#00FF41]"
                  />
                )}
                <span className="text-sm text-[#00FF41]">{user.name || user.email}</span>
              </div>
            )}
            <Button
              onClick={() => setShowTaxDialog(true)}
              className="matrix-btn rounded-none text-xs h-8 border-yellow-500 text-yellow-500 hover:bg-yellow-500/20"
              data-testid="tax-report-btn"
            >
              <FileText className="h-3 w-3 mr-1" strokeWidth={1.5} />
              TAX REPORT
            </Button>
            <Button
              onClick={fetchAllData}
              disabled={refreshing}
              className="matrix-btn rounded-none text-xs h-8"
              data-testid="refresh-stats-btn"
            >
              <RefreshCw className={`h-3 w-3 mr-1 ${refreshing ? 'animate-spin' : ''}`} strokeWidth={1.5} />
              REFRESH
            </Button>
          </div>
        </div>
      </header>

      {/* Tax Report Dialog */}
      <Dialog open={showTaxDialog} onOpenChange={setShowTaxDialog}>
        <DialogContent className="bg-black border-2 border-[#00FF41] text-[#00FF41] max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-mono text-[#00FF41] flex items-center gap-2">
              <FileText className="h-5 w-5" />
              CRYPTOCURRENCY TAX REPORT
            </DialogTitle>
            <DialogDescription className="text-[#008F11]">
              Generate comprehensive tax reports with multiple cost basis methods and export formats
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Date Range Options */}
            <div className="bg-[#001500] border border-[#003B00] p-4 rounded">
              <div className="flex items-center gap-4 mb-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={!useCustomDates}
                    onChange={() => setUseCustomDates(false)}
                    className="accent-[#00FF41]"
                  />
                  <span className="text-[#008F11]">Tax Year</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={useCustomDates}
                    onChange={() => setUseCustomDates(true)}
                    className="accent-[#00FF41]"
                  />
                  <span className="text-[#008F11]">Custom Date Range</span>
                </label>
              </div>
              
              {!useCustomDates ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[#008F11]">Tax Year</Label>
                    <Input
                      type="number"
                      value={taxYear}
                      onChange={(e) => setTaxYear(parseInt(e.target.value))}
                      min="2020"
                      max={new Date().getFullYear()}
                      className="bg-[#001500] border-[#003B00] text-[#00FF41] font-mono"
                    />
                  </div>
                  <div>
                    <Label className="text-[#008F11]">State (optional)</Label>
                    <Input
                      type="text"
                      value={taxState}
                      onChange={(e) => setTaxState(e.target.value.toUpperCase().slice(0, 2))}
                      placeholder="e.g., CA"
                      maxLength={2}
                      className="bg-[#001500] border-[#003B00] text-[#00FF41] font-mono uppercase"
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[#008F11]">Start Date</Label>
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="bg-[#001500] border-[#003B00] text-[#00FF41] font-mono"
                    />
                  </div>
                  <div>
                    <Label className="text-[#008F11]">End Date</Label>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="bg-[#001500] border-[#003B00] text-[#00FF41] font-mono"
                    />
                  </div>
                </div>
              )}
            </div>
            
            {/* Cost Basis Method & Filter */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#008F11]">Cost Basis Method</Label>
                <select
                  value={costBasisMethod}
                  onChange={(e) => setCostBasisMethod(e.target.value)}
                  className="w-full bg-[#001500] border border-[#003B00] text-[#00FF41] font-mono p-2 rounded"
                >
                  <option value="fifo">FIFO (First In, First Out)</option>
                  <option value="lifo">LIFO (Last In, First Out)</option>
                  <option value="hifo">HIFO (Highest In, First Out)</option>
                  <option value="average">Average Cost</option>
                </select>
              </div>
              <div>
                <Label className="text-[#008F11]">Filter by Coin (optional)</Label>
                <select
                  value={coinFilter}
                  onChange={(e) => setCoinFilter(e.target.value)}
                  className="w-full bg-[#001500] border border-[#003B00] text-[#00FF41] font-mono p-2 rounded"
                >
                  <option value="">All Coins</option>
                  <option value="BTC">BTC - Bitcoin</option>
                  <option value="ETH">ETH - Ethereum</option>
                  <option value="SOL">SOL - Solana</option>
                  <option value="LTC">LTC - Litecoin</option>
                  <option value="DOGE">DOGE - Dogecoin</option>
                  <option value="BCH">BCH - Bitcoin Cash</option>
                  <option value="XRP">XRP - Ripple</option>
                  <option value="SHIB">SHIB - Shiba Inu</option>
                </select>
              </div>
            </div>
            
            <Button
              onClick={generateTaxReport}
              disabled={generatingReport}
              className="w-full bg-[#003B00] hover:bg-[#005500] text-[#00FF41] border border-[#00FF41]"
            >
              {generatingReport ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  GENERATING...
                </>
              ) : (
                <>
                  <FileText className="h-4 w-4 mr-2" />
                  GENERATE REPORT
                </>
              )}
            </Button>
            
            {/* Report Display */}
            {taxReport && (
              <div className="space-y-4 border-t border-[#003B00] pt-4">
                <div className="flex justify-between items-center flex-wrap gap-2">
                  <h3 className="text-lg font-mono text-yellow-500">
                    Report Generated ({taxReport.cost_basis_method} Method)
                  </h3>
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      onClick={downloadTaxReport}
                      variant="outline"
                      size="sm"
                      className="border-[#003B00] text-[#00FF41] hover:bg-[#003B00]"
                    >
                      <Download className="h-4 w-4 mr-1" />
                      TXT
                    </Button>
                    <Button
                      onClick={() => downloadCSV('standard')}
                      variant="outline"
                      size="sm"
                      className="border-[#003B00] text-[#00FF41] hover:bg-[#003B00]"
                    >
                      <Download className="h-4 w-4 mr-1" />
                      CSV
                    </Button>
                    <Button
                      onClick={() => downloadCSV('turbotax')}
                      variant="outline"
                      size="sm"
                      className="border-cyan-600 text-cyan-400 hover:bg-cyan-900/30"
                    >
                      <Download className="h-4 w-4 mr-1" />
                      TurboTax
                    </Button>
                    <Button
                      onClick={() => downloadCSV('taxact')}
                      variant="outline"
                      size="sm"
                      className="border-purple-600 text-purple-400 hover:bg-purple-900/30"
                    >
                      <Download className="h-4 w-4 mr-1" />
                      TaxAct
                    </Button>
                    <Button
                      onClick={() => downloadCSV('cointracker')}
                      variant="outline"
                      size="sm"
                      className="border-orange-600 text-orange-400 hover:bg-orange-900/30"
                    >
                      <Download className="h-4 w-4 mr-1" />
                      CoinTracker
                    </Button>
                    <Button
                      onClick={printTaxReport}
                      variant="outline"
                      size="sm"
                      className="border-[#003B00] text-[#00FF41] hover:bg-[#003B00]"
                    >
                      <Printer className="h-4 w-4 mr-1" />
                      PRINT
                    </Button>
                  </div>
                </div>
                
                {/* Disclaimer */}
                <div className="bg-yellow-500/20 border border-yellow-500 p-3 rounded text-yellow-500 text-sm">
                  <strong>DISCLAIMER:</strong> {taxReport.disclaimer}
                </div>
                
                {/* Federal Summary */}
                <div className="bg-[#001500] border border-[#003B00] p-4 rounded">
                  <h4 className="text-[#00FF41] font-mono mb-3 flex items-center gap-2">
                    <DollarSign className="h-4 w-4" />
                    FEDERAL TAX SUMMARY
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                    <div>
                      <span className="text-[#008F11]">Total Transactions: </span>
                      <span className="text-cyan-400">{taxReport.federal_summary?.total_transactions}</span>
                    </div>
                    <div>
                      <span className="text-[#008F11]">Total Buys: </span>
                      <span className="text-cyan-400">{taxReport.federal_summary?.total_buys}</span>
                    </div>
                    <div>
                      <span className="text-[#008F11]">Total Sells: </span>
                      <span className="text-orange-400">{taxReport.federal_summary?.total_sells}</span>
                    </div>
                    <div>
                      <span className="text-[#008F11]">Total Proceeds: </span>
                      <span className="text-[#00FF41]">${taxReport.federal_summary?.total_proceeds?.toLocaleString('en-US', {minimumFractionDigits: 2})}</span>
                    </div>
                    <div>
                      <span className="text-[#008F11]">Total Cost Basis: </span>
                      <span className="text-[#00FF41]">${taxReport.federal_summary?.total_cost_basis?.toLocaleString('en-US', {minimumFractionDigits: 2})}</span>
                    </div>
                    <div>
                      <span className="text-[#008F11]">Net Gain/Loss: </span>
                      <span className={taxReport.federal_summary?.net_realized_gain_loss >= 0 ? 'text-green-500' : 'text-red-500'}>
                        ${taxReport.federal_summary?.net_realized_gain_loss?.toLocaleString('en-US', {minimumFractionDigits: 2})}
                      </span>
                    </div>
                  </div>
                  
                  {/* Short/Long Term Breakdown */}
                  <div className="mt-3 pt-3 border-t border-[#003B00] grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-[#008F11]">Short-term (&lt;1 yr): </span>
                      <span className={taxReport.federal_summary?.short_term_gain_loss >= 0 ? 'text-green-500' : 'text-red-500'}>
                        ${taxReport.federal_summary?.short_term_gain_loss?.toLocaleString('en-US', {minimumFractionDigits: 2})}
                      </span>
                    </div>
                    <div>
                      <span className="text-[#008F11]">Long-term (&gt;1 yr): </span>
                      <span className={taxReport.federal_summary?.long_term_gain_loss >= 0 ? 'text-green-500' : 'text-red-500'}>
                        ${taxReport.federal_summary?.long_term_gain_loss?.toLocaleString('en-US', {minimumFractionDigits: 2})}
                      </span>
                    </div>
                  </div>
                  
                  {/* Wash Sales Warning */}
                  {taxReport.federal_summary?.wash_sale_disallowed > 0 && (
                    <div className="mt-3 p-2 bg-red-900/30 border border-red-500 rounded text-red-400 text-sm">
                      <strong>WASH SALE ALERT:</strong> ${taxReport.federal_summary?.wash_sale_disallowed?.toFixed(2)} in losses may be disallowed due to wash sale rules. {taxReport.total_wash_sales} potential wash sales detected.
                    </div>
                  )}
                </div>
                
                {/* Estimated Tax Liability */}
                {taxReport.estimated_tax && taxReport.federal_summary?.net_realized_gain_loss > 0 && (
                  <div className="bg-[#001500] border border-yellow-600 p-4 rounded">
                    <h4 className="text-yellow-500 font-mono mb-2">ESTIMATED FEDERAL TAX LIABILITY</h4>
                    <div className="text-2xl font-mono text-yellow-400">
                      ${taxReport.estimated_tax.federal_estimate?.toLocaleString('en-US', {minimumFractionDigits: 2})}
                    </div>
                    <p className="text-xs text-yellow-600 mt-1">{taxReport.estimated_tax.note}</p>
                  </div>
                )}
                
                {/* Unrealized Gains */}
                {taxReport.unrealized_gains && taxReport.unrealized_gains.length > 0 && (
                  <div className="bg-[#001500] border border-[#003B00] p-4 rounded">
                    <h4 className="text-[#00FF41] font-mono mb-3">UNREALIZED GAINS/LOSSES (Current Holdings)</h4>
                    <div className="space-y-2">
                      {taxReport.unrealized_gains.map((item) => (
                        <div key={item.coin} className="flex items-center justify-between text-sm border-b border-[#003B00] pb-2">
                          <div>
                            <span className="text-cyan-400 font-mono">{item.coin}</span>
                            <span className="text-[#006600] ml-2 text-xs">{item.quantity} @ ${item.current_price}</span>
                          </div>
                          <div className="flex gap-4">
                            <span className="text-[#008F11]">Value: ${item.current_value?.toLocaleString()}</span>
                            <span className="text-[#008F11]">Cost: ${item.cost_basis?.toLocaleString()}</span>
                            <span className={item.unrealized_gain_loss >= 0 ? 'text-green-500' : 'text-red-500'}>
                              {item.unrealized_gain_loss >= 0 ? '+' : ''}${item.unrealized_gain_loss?.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 pt-2 border-t border-[#003B00] flex justify-between">
                      <span className="text-[#008F11]">Total Unrealized:</span>
                      <span className={taxReport.total_unrealized_gain_loss >= 0 ? 'text-green-500 font-mono' : 'text-red-500 font-mono'}>
                        ${taxReport.total_unrealized_gain_loss?.toLocaleString('en-US', {minimumFractionDigits: 2})}
                      </span>
                    </div>
                  </div>
                )}
                
                {/* State Info */}
                {taxReport.state_info?.state && (
                  <div className="bg-[#001500] border border-[#003B00] p-4 rounded">
                    <h4 className="text-[#00FF41] font-mono mb-2">STATE TAX INFO ({taxReport.state_info.state})</h4>
                    <p className="text-sm text-[#008F11]">{taxReport.state_info.note}</p>
                  </div>
                )}
                
                {/* Quarterly */}
                <div className="bg-[#001500] border border-[#003B00] p-4 rounded">
                  <h4 className="text-[#00FF41] font-mono mb-3">QUARTERLY BREAKDOWN</h4>
                  <div className="grid grid-cols-4 gap-3 text-sm">
                    <div className="text-center">
                      <div className="text-[#008F11] text-xs">Q1 (Jan-Mar)</div>
                      <div className={taxReport.quarterly_breakdown?.Q1_Jan_Mar >= 0 ? 'text-green-500' : 'text-red-500'}>
                        ${taxReport.quarterly_breakdown?.Q1_Jan_Mar?.toLocaleString('en-US', {minimumFractionDigits: 2})}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-[#008F11] text-xs">Q2 (Apr-Jun)</div>
                      <div className={taxReport.quarterly_breakdown?.Q2_Apr_Jun >= 0 ? 'text-green-500' : 'text-red-500'}>
                        ${taxReport.quarterly_breakdown?.Q2_Apr_Jun?.toLocaleString('en-US', {minimumFractionDigits: 2})}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-[#008F11] text-xs">Q3 (Jul-Sep)</div>
                      <div className={taxReport.quarterly_breakdown?.Q3_Jul_Sep >= 0 ? 'text-green-500' : 'text-red-500'}>
                        ${taxReport.quarterly_breakdown?.Q3_Jul_Sep?.toLocaleString('en-US', {minimumFractionDigits: 2})}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-[#008F11] text-xs">Q4 (Oct-Dec)</div>
                      <div className={taxReport.quarterly_breakdown?.Q4_Oct_Dec >= 0 ? 'text-green-500' : 'text-red-500'}>
                        ${taxReport.quarterly_breakdown?.Q4_Oct_Dec?.toLocaleString('en-US', {minimumFractionDigits: 2})}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Crypto Summary */}
                {taxReport.crypto_summary && Object.keys(taxReport.crypto_summary).length > 0 && (
                  <div className="bg-[#001500] border border-[#003B00] p-4 rounded">
                    <h4 className="text-[#00FF41] font-mono mb-3">BY CRYPTOCURRENCY</h4>
                    <div className="space-y-2">
                      {Object.entries(taxReport.crypto_summary).map(([coin, data]) => (
                        <div key={coin} className="flex items-center justify-between text-sm border-b border-[#003B00] pb-2">
                          <span className="text-cyan-400 font-mono">{coin}</span>
                          <div className="flex gap-4">
                            <span className="text-[#008F11]">Buys: {data.total_buys}</span>
                            <span className="text-[#008F11]">Sells: {data.total_sells}</span>
                            <span className={data.realized_gain_loss >= 0 ? 'text-green-500' : 'text-red-500'}>
                              ${data.realized_gain_loss?.toLocaleString('en-US', {minimumFractionDigits: 2})}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Form 8949 Preview */}
                {taxReport.form_8949_entries && taxReport.form_8949_entries.length > 0 && (
                  <div className="bg-[#001500] border border-[#003B00] p-4 rounded">
                    <h4 className="text-[#00FF41] font-mono mb-3">
                      FORM 8949 ENTRIES (Showing {Math.min(10, taxReport.form_8949_entries.length)} of {taxReport.total_form_8949_entries})
                    </h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-[#008F11] border-b border-[#003B00]">
                            <th className="text-left p-1">Asset</th>
                            <th className="text-left p-1">Acquired</th>
                            <th className="text-left p-1">Sold</th>
                            <th className="text-right p-1">Proceeds</th>
                            <th className="text-right p-1">Cost Basis</th>
                            <th className="text-right p-1">Gain/Loss</th>
                            <th className="text-center p-1">Term</th>
                          </tr>
                        </thead>
                        <tbody>
                          {taxReport.form_8949_entries.slice(0, 10).map((entry, idx) => (
                            <tr key={idx} className="border-b border-[#002200]">
                              <td className="p-1 text-cyan-400">{entry.description}</td>
                              <td className="p-1 text-[#00FF41]">{entry.date_acquired}</td>
                              <td className="p-1 text-[#00FF41]">{entry.date_sold}</td>
                              <td className="p-1 text-right">${entry.proceeds?.toFixed(2)}</td>
                              <td className="p-1 text-right">${entry.cost_basis?.toFixed(2)}</td>
                              <td className={`p-1 text-right ${entry.gain_loss >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                ${entry.gain_loss?.toFixed(2)}
                              </td>
                              <td className="p-1 text-center">
                                <span className={entry.term === 'Long-term' ? 'text-purple-400' : 'text-orange-400'}>
                                  {entry.term}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-xs text-[#006600] mt-2">
                      Download CSV for complete Form 8949 data
                    </p>
                  </div>
                )}
                
                {/* Wash Sales Detail */}
                {taxReport.wash_sales && taxReport.wash_sales.length > 0 && (
                  <div className="bg-red-900/20 border border-red-600 p-4 rounded">
                    <h4 className="text-red-400 font-mono mb-3">POTENTIAL WASH SALES DETECTED ({taxReport.total_wash_sales})</h4>
                    <div className="space-y-2 text-sm">
                      {taxReport.wash_sales.slice(0, 5).map((ws, idx) => (
                        <div key={idx} className="flex justify-between text-red-300">
                          <span>{ws.coin}: Sold {ws.sell_date} (loss ${ws.sell_loss})</span>
                          <span>Rebought {ws.buy_date} ({ws.days_apart} days)</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-red-500 mt-2">
                      Wash sale rule: Cannot claim loss if you buy same asset within 30 days before/after sale.
                    </p>
                  </div>
                )}
                
                {/* Simulation Note */}
                <div className="text-xs text-[#006600]">
                  Note: {taxReport.simulation_note?.simulation_trades || 0} simulation trades excluded (not taxable)
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button
              onClick={() => {
                setShowTaxDialog(false);
                setTaxReport(null);
              }}
              variant="outline"
              className="border-[#003B00] text-[#00FF41] hover:bg-[#003B00]"
            >
              CLOSE
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Main Content */}
      <main className="max-w-[1920px] mx-auto p-4">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="h-8 w-8 animate-spin text-[#00FF41]" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Key Stats Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {/* Total Trades */}
              <Card className="matrix-card rounded-none" data-testid="total-trades-card">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs uppercase text-[#008F11]">TOTAL TRADES</span>
                    <BarChart3 className="h-4 w-4 text-[#008F11]" strokeWidth={1.5} />
                  </div>
                  <p className="text-2xl font-bold glow-text">{stats?.total_trades || 0}</p>
                </CardContent>
              </Card>

              {/* Win Rate */}
              <Card className="matrix-card rounded-none" data-testid="win-rate-card">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs uppercase text-[#008F11]">WIN RATE</span>
                    <Percent className="h-4 w-4 text-[#008F11]" strokeWidth={1.5} />
                  </div>
                  <p className="text-2xl font-bold glow-text">{stats?.win_rate || 0}%</p>
                  <p className="text-xs text-[#008F11]">
                    {stats?.winning_trades || 0}W / {stats?.losing_trades || 0}L
                  </p>
                </CardContent>
              </Card>

              {/* Best Trade */}
              <Card className="matrix-card rounded-none" data-testid="best-trade-card">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs uppercase text-[#008F11]">BEST TRADE</span>
                    <Trophy className="h-4 w-4 text-yellow-500" strokeWidth={1.5} />
                  </div>
                  <p className="text-2xl font-bold text-[#00FF41]">
                    {formatProfit(stats?.best_trade)}
                  </p>
                </CardContent>
              </Card>

              {/* Worst Trade */}
              <Card className="matrix-card rounded-none" data-testid="worst-trade-card">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs uppercase text-[#008F11]">WORST TRADE</span>
                    <TrendingDown className="h-4 w-4 text-red-500" strokeWidth={1.5} />
                  </div>
                  <p className={`text-2xl font-bold ${(stats?.worst_trade || 0) >= 0 ? 'text-[#00FF41]' : 'text-red-500'}`}>
                    {formatProfit(stats?.worst_trade)}
                  </p>
                </CardContent>
              </Card>

              {/* Lifetime Profit */}
              <Card className="matrix-card rounded-none" data-testid="lifetime-profit-card">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs uppercase text-[#008F11]">LIFETIME PROFIT</span>
                    <DollarSign className="h-4 w-4 text-[#008F11]" strokeWidth={1.5} />
                  </div>
                  <p className={`text-2xl font-bold ${(lifetimeStats?.total_profit || 0) >= 0 ? 'text-[#00FF41]' : 'text-red-500'}`}>
                    {formatProfit(lifetimeStats?.total_profit)}
                  </p>
                  <p className="text-xs text-[#008F11]">
                    Since {lifetimeStats?.reset_date ? new Date(lifetimeStats.reset_date).toLocaleDateString() : 'N/A'}
                  </p>
                </CardContent>
              </Card>

              {/* Lifetime Skim */}
              <Card className="matrix-card rounded-none" data-testid="lifetime-skim-card">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs uppercase text-[#008F11]">LIFETIME SKIM</span>
                    <Award className="h-4 w-4 text-cyan-500" strokeWidth={1.5} />
                  </div>
                  <p className="text-2xl font-bold text-cyan-400">
                    {formatCurrency(lifetimeStats?.total_skim || 0)}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Badges Section */}
            {badges && (badges.simulation?.length > 0 || badges.live?.length > 0) && (
              <Card className="matrix-card rounded-none" data-testid="badges-section">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm uppercase text-[#008F11] flex items-center gap-2">
                    <Award className="h-4 w-4" strokeWidth={1.5} />
                    EARNED BADGES ({(badges.simulation?.length || 0) + (badges.live?.length || 0)} / {(badges.total_definitions || 13) * 2})
                    {showcaseBadges.length > 0 && (
                      <span className="text-xs text-yellow-500 ml-2">({showcaseBadges.length}/3 showcased)</span>
                    )}
                  </CardTitle>
                  <p className="text-[10px] text-[#006B11] mt-1">Click any badge to pin it to your leaderboard showcase (max 3)</p>
                </CardHeader>
                <CardContent>
                  {badges.simulation?.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs text-blue-400 mb-2 uppercase tracking-wider">Simulation Mode — {badges.simulation.length} badges</p>
                      <div className="flex flex-wrap gap-2">
                        {badges.simulation.map((b, i) => {
                          const isNeg = b.category === 'negative' || b.category === 'fun';
                          const isPinned = showcaseBadges.includes(b.id);
                          return (
                            <button
                              key={`sim-${i}`}
                              onClick={() => toggleShowcaseBadge(b.id)}
                              className={`px-3 py-1.5 border text-xs font-mono flex items-center gap-1.5 transition-all cursor-pointer ${
                                isPinned
                                  ? 'border-yellow-500 bg-yellow-900/40 text-yellow-300 ring-1 ring-yellow-500/50'
                                  : isNeg 
                                    ? 'border-red-700 bg-red-900/30 text-red-400 hover:border-red-500' 
                                    : 'border-[#003B00] bg-[#001a00] text-[#00FF41] hover:border-[#00FF41]'
                              }`}
                              title={isPinned ? `Unpin "${b.name}" from showcase` : `Pin "${b.name}" to showcase`}
                              data-testid={`badge-sim-${b.id}`}
                            >
                              {isPinned && <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />}
                              {b.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {badges.live?.length > 0 && (
                    <div>
                      <p className="text-xs text-red-400 mb-2 uppercase tracking-wider">Live Mode — {badges.live.length} badges</p>
                      <div className="flex flex-wrap gap-2">
                        {badges.live.map((b, i) => {
                          const isNeg = b.category === 'negative' || b.category === 'fun';
                          const isPinned = showcaseBadges.includes(b.id);
                          return (
                            <button
                              key={`live-${i}`}
                              onClick={() => toggleShowcaseBadge(b.id)}
                              className={`px-3 py-1.5 border text-xs font-mono flex items-center gap-1.5 transition-all cursor-pointer ${
                                isPinned
                                  ? 'border-yellow-500 bg-yellow-900/40 text-yellow-300 ring-1 ring-yellow-500/50'
                                  : isNeg
                                    ? 'border-red-700 bg-red-900/30 text-red-400 hover:border-red-500'
                                    : 'border-yellow-700 bg-yellow-900/30 text-yellow-400 hover:border-yellow-500'
                              }`}
                              title={isPinned ? `Unpin "${b.name}" from showcase` : `Pin "${b.name}" to showcase`}
                              data-testid={`badge-live-${b.id}`}
                            >
                              {isPinned && <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />}
                              {b.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Profit by Hour */}
              <Card className="matrix-card rounded-none" data-testid="profit-by-hour-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm uppercase text-[#008F11] flex items-center gap-2">
                    <Clock className="h-4 w-4" strokeWidth={1.5} />
                    PROFIT BY HOUR OF DAY
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={profitByHour}>
                        <XAxis 
                          dataKey="hour" 
                          tick={{ fill: '#008F11', fontSize: 10 }}
                          axisLine={{ stroke: '#003B00' }}
                          tickLine={{ stroke: '#003B00' }}
                          interval={2}
                        />
                        <YAxis 
                          tick={{ fill: '#008F11', fontSize: 10 }}
                          axisLine={{ stroke: '#003B00' }}
                          tickLine={{ stroke: '#003B00' }}
                          tickFormatter={(v) => `$${v.toFixed(2)}`}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar 
                          dataKey="profit" 
                          fill={HOUR_COLORS}
                          radius={[2, 2, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="mt-2 text-xs text-[#008F11] text-center">
                    Best Hour: {profitByHour.length > 0 ? 
                      profitByHour.reduce((max, h) => h.profit > max.profit ? h : max, profitByHour[0])?.hour : 'N/A'}
                  </div>
                </CardContent>
              </Card>

              {/* Profit by Coin */}
              <Card className="matrix-card rounded-none" data-testid="profit-by-coin-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm uppercase text-[#008F11] flex items-center gap-2">
                    <PieChartIcon className="h-4 w-4" strokeWidth={1.5} />
                    PROFIT BY COIN
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={profitByPair}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={2}
                          dataKey="value"
                          nameKey="name"
                          label={({ name, profit }) => `${name}: ${formatProfit(profit)}`}
                          labelLine={{ stroke: '#00FF41', strokeWidth: 1 }}
                        >
                          {profitByPair.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill || FALLBACK_COLORS[index % FALLBACK_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip 
                          formatter={(value, name, props) => [formatProfit(props.payload.profit), name]}
                          contentStyle={{ background: '#001a00', border: '1px solid #00FF41', color: '#00FF41' }}
                          labelStyle={{ color: '#00FF41' }}
                          itemStyle={{ color: '#00FF41' }}
                        />
                        <Legend 
                          formatter={(value, entry) => (
                            <span style={{ color: entry.color }}>{value}</span>
                          )}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Trade History */}
            <Card className="matrix-card rounded-none" data-testid="trade-history-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm uppercase text-[#008F11] flex items-center gap-2">
                  <Target className="h-4 w-4" strokeWidth={1.5} />
                  ALL TRADES ({trades.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-80">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-[#0a0a0a] border-b border-[#003B00]">
                      <tr className="text-[#008F11]">
                        <th className="text-left p-2">TIME</th>
                        <th className="text-left p-2">TYPE</th>
                        <th className="text-left p-2">PAIR</th>
                        <th className="text-right p-2">PRICE</th>
                        <th className="text-right p-2">AMOUNT</th>
                        <th className="text-right p-2">VALUE</th>
                        <th className="text-right p-2">PROFIT</th>
                        <th className="text-left p-2">TRIGGER</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trades.slice().reverse().map((trade, i) => (
                        <tr 
                          key={trade.id || i} 
                          className="border-b border-[#003B00]/30 hover:bg-[#003B00]/20 cursor-pointer"
                          onClick={() => setSelectedTrade(trade)}
                          data-testid={`stats-trade-row-${i}`}
                        >
                          <td className="p-2 text-[#008F11]">
                            {trade.timestamp ? new Date(trade.timestamp).toLocaleString(undefined, {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: true,
                            }) : 'N/A'}
                          </td>
                          <td className="p-2">
                            <Badge 
                              className={`rounded-none text-[10px] ${
                                trade.type === 'BUY' 
                                  ? 'bg-green-900/30 text-[#00FF41] border-[#00FF41]' 
                                  : 'bg-red-900/30 text-red-400 border-red-400'
                              }`}
                            >
                              {trade.type}
                            </Badge>
                          </td>
                          <td className="p-2">{trade.pair || 'BTC-USD'}</td>
                          <td className="p-2 text-right font-mono">
                            ${parseFloat(trade.price || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="p-2 text-right font-mono">
                            {parseFloat(trade.amount || 0).toFixed(8)}
                          </td>
                          <td className="p-2 text-right font-mono">
                            ${parseFloat(trade.usd_value || 0).toFixed(2)}
                          </td>
                          <td className={`p-2 text-right font-mono ${
                            trade.type === 'SELL' 
                              ? (trade.profit || 0) >= 0 ? 'text-[#00FF41]' : 'text-red-500'
                              : 'text-[#008F11]'
                          }`}>
                            {trade.type === 'SELL' ? formatProfit(trade.profit) : '--'}
                          </td>
                          <td className="p-2">
                            <span className="text-[#008F11]">{trade.trigger || 'RSI'}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {trades.length === 0 && (
                    <div className="text-center py-8 text-[#008F11]">
                      No trades recorded yet
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-[#003B00] p-3 mt-4" style={{ background: 'rgba(5, 5, 5, 0.9)' }}>
        <div className="max-w-[1920px] mx-auto flex flex-col items-center gap-2 text-[#008F11] text-xs">
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
            <a href="/about" className="hover:text-[#00FF41] transition-colors" data-testid="footer-link-about">About</a>
            <span className="text-[#003B00]">|</span>
            <a href="/faq" className="hover:text-[#00FF41] transition-colors" data-testid="footer-link-faq">FAQ</a>
            <span className="text-[#003B00]">|</span>
            <a href="/contact" className="hover:text-[#00FF41] transition-colors" data-testid="footer-link-contact">Contact</a>
            <span className="text-[#003B00]">|</span>
            <a href="/privacy" className="hover:text-[#00FF41] transition-colors" data-testid="footer-link-privacy">Privacy</a>
            <span className="text-[#003B00]">|</span>
            <a href="/terms" className="hover:text-[#00FF41] transition-colors" data-testid="footer-link-terms">Terms</a>
            <span className="text-[#003B00]">|</span>
            <a href="/disclaimer" className="hover:text-[#00FF41] transition-colors" data-testid="footer-link-disclaimer">Disclaimer</a>
            <span className="text-[#003B00]">|</span>
            <a href="/cookies" className="hover:text-[#00FF41] transition-colors" data-testid="footer-link-cookies">Cookies</a>
            <span className="text-[#003B00]">|</span>
            <a href="/changelog" className="hover:text-[#00FF41] transition-colors" data-testid="footer-link-changelog">Changelog</a>
          </div>
          <span>BITBOT V3.9.32 [LIVE] | USER STATISTICS</span>
        </div>
      </footer>

      {/* Trade Detail Modal */}
      {selectedTrade && (
        <TradeDetailModal
          trade={selectedTrade}
          onClose={() => setSelectedTrade(null)}
          feeRate={feeRate}
        />
      )}
    </div>
  );
};

export default UserStatsPage;
